import { useEffect, useState } from 'react';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { AuthRequiredError, unwrap } from '@truecare/api-client';
import { Alert, Button, Card, CardHeader, StatusBadge } from '@truecare/ui';
import { api } from '../api';
import { SUPPORT_EMAIL } from '../support-email';
import { personaLabelForRoles } from '../lib/persona-label';
import { resolveAdminUrl } from '../lib/next-redirect';
import { usePersonaHome } from '../auth/use-persona-home';
import { BuyerShiftPanel } from '../components/BuyerShiftPanel';
import {
  HomecareAgreementCard,
  useHomecareAgreement,
} from '../components/HomecareAgreementCard';
import { BuyerInvoicesTab } from '../components/BuyerInvoicesTab';
import { FacilityUsersTab } from '../components/FacilityUsersTab';
import { HomecarePaymentMethods } from '../components/HomecarePaymentMethods';
import { formatDate } from '../lib/format-date';
import { caregiverBannerFlags, joinAnd } from '../lib/caregiver-banner';
import { CaregiverAvailabilitySection } from '../components/CaregiverAvailabilitySection';
import { CaregiverPaymentTaxSection } from '../components/CaregiverPaymentTaxSection';
import { AccountProfileCard } from '../components/AccountProfileCard';
import { CaregiverComplianceSection } from '../components/CaregiverComplianceSection';
import { CaregiverCertificatesSection } from '../components/CaregiverCertificatesSection';
import { CaregiverCredentialsSection } from '../components/CaregiverCredentialsSection';
import { CaregiverDocsCompletionNotice } from '../components/CaregiverDocsCompletionNotice';
import { CaregiverSignedAgreementCard } from '../components/CaregiverSignedAgreementCard';
import { caregiverSectionGates } from '../components/caregiver-section-gates';
import { CaregiverOnboardingTab } from '../components/CaregiverOnboardingTab';
import { PayoutsTab } from '../components/CaregiverPayoutsPanel';
import { CaregiverProfileEditForm } from '../components/CaregiverProfileEditForm';
import { CaregiverProfilePhoto } from '../components/CaregiverProfilePhoto';
import { CaregiverWorkExperienceSection } from '../components/CaregiverWorkExperienceSection';
import { CaregiverSkillsSection } from '../components/CaregiverSkillsSection';
import { CaregiverImmunizationSection } from '../components/CaregiverImmunizationSection';
import { OffersTab, ScheduleTab } from '../components/CaregiverShiftPanel';
import { CaregiverSubmitPanel } from '../components/CaregiverSubmitPanel';
import {
  PersonaWorkspace,
  type PersonaWorkspaceTab,
} from '../components/PersonaWorkspace';
import {
  HomecareAccountSummary,
  RecipientsSection,
} from '../components/HomecareProfile';
import { PortalShell } from '../components/PortalShell';
import { StatusBanner } from '../components/StatusBanner';
import type { CaregiverStatus, FacilityCapability, Gender, HomecareMeResponse } from '@truecare/schemas';
import { genderLabel } from '@truecare/schemas';
import { facilityWorkspaceTabPlan } from '../lib/facility-workspace-tabs';

export const Route = createFileRoute('/me')({
  /**
   * Pre-load gate: bounce signed-out users to /sign-in before any UI
   * renders. Returns the /auth/me payload so the component doesn't
   * re-fetch on first paint.
   */
  beforeLoad: async () => {
    try {
      const me = await unwrap(api.GET('/api/v1/auth/me'));
      return { me };
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        throw redirect({ to: '/sign-in' });
      }
      throw err;
    }
  },
  component: MePage,
});

function MePage() {
  const { me } = Route.useRouteContext();

  // Persona-aware /me. v1 has one persona-branch each for caregiver
  // (Phase 5/6d), facility user (Phase 7c), and homecare client
  // (Phase 7f). Operator surface lives at apps/admin (Phase 14a) —
  // when an operator-only user lands here the welcome card surfaces a
  // link to the admin app. Personal account/profile lives at /profile,
  // reached from the top-right user menu (Phase 17).
  const {
    isCaregiver,
    isFacilityUser,
    isHomecareClient,
    isOperator,
    caregiverQuery,
    facilityQuery,
    homecareQuery,
  } = usePersonaHome(me);
  // Operator tooling lives at apps/admin (Phase 14-A). `resolveAdminUrl`
  // resolves it via env override → runtime `admin.<domain>` derivation, so
  // staging/prod need no baked admin host in the portal build.
  const adminUrl = resolveAdminUrl();
  // An operator who lands on /me (e.g. clicks "Sign in" from marketing while
  // already authenticated) has no portal surface here — bounce them straight
  // to the admin app instead of parking them on a dead-end placeholder card.
  // Only fires for operator-ONLY users: dual-role operators render their
  // customer persona panel above and never reach this card. `.replace` keeps
  // the back button from looping back into this page.
  const isOperatorOnly =
    isOperator && !isCaregiver && !isFacilityUser && !isHomecareClient;
  useEffect(() => {
    if (isOperatorOnly && adminUrl) {
      window.location.replace(adminUrl);
    }
  }, [isOperatorOnly, adminUrl]);

  return (
    <PortalShell>
      <div className="mx-auto max-w-3xl px-6 py-10 md:py-16">
        {/* Caregiver, facility + homecare render their own heading inside
            <PersonaWorkspace>; the shared heading stays only for welcome. */}
        {!isCaregiver && !isFacilityUser && !isHomecareClient && (
          <>
            <h1 className="font-heading text-3xl md:text-4xl text-forest">
              My Profile
            </h1>
            <p className="mt-2 font-body text-sm text-ink-muted">
              Signed in as {me.email}
            </p>
          </>
        )}

        {!isCaregiver && !isFacilityUser && !isHomecareClient && (
          <Card padding="lg" className="mt-8">
            <CardHeader
              title="Welcome back"
              description={`Your account roles: ${me.roles.map((r: { persona: string }) => r.persona).join(', ') || 'none'}.`}
            />
            {isOperator && adminUrl ? (
              // The effect above is redirecting operator-only users to the
              // admin app; this copy is what shows for the split second before
              // the browser navigates (and as a manual fallback link).
              <p className="font-body text-sm text-ink-muted">
                Taking you to the admin app…{' '}
                <a
                  href={adminUrl}
                  className="font-semibold text-forest underline underline-offset-2 hover:text-forest-dark"
                >
                  Open admin dashboard →
                </a>
              </p>
            ) : isOperator ? (
              <p className="font-body text-sm text-ink-muted">
                Operator tooling lives in the admin app. (Admin URL not
                configured for this environment — contact the operator
                administrator if you reached this page in error.)
              </p>
            ) : (
              <p className="font-body text-sm text-ink-muted">
                Your full portal surface depends on your role on the
                platform. This is a minimal sign-in confirmation for
                accounts without an assigned persona.
              </p>
            )}
          </Card>
        )}

        {isCaregiver && caregiverQuery.isLoading && (
          <p className="mt-8 font-body text-ink-muted">Loading your profile…</p>
        )}

        {isCaregiver && caregiverQuery.data && (
          <CaregiverWorkspace data={caregiverQuery.data} />
        )}

        {isCaregiver && caregiverQuery.isError && (
          <Alert tone="danger" title="Couldn't load your profile">
            Please refresh the page. If this keeps happening, contact us at{' '}
            {SUPPORT_EMAIL}.
          </Alert>
        )}

        {isFacilityUser && facilityQuery.isLoading && (
          <p className="mt-8 font-body text-ink-muted">Loading your facility…</p>
        )}

        {isFacilityUser && facilityQuery.data && (
          <FacilityWorkspace data={facilityQuery.data} />
        )}

        {isFacilityUser && facilityQuery.isError && (
          <Alert tone="danger" title="Couldn't load your facility">
            Please refresh the page. If this keeps happening, contact us at{' '}
            {SUPPORT_EMAIL}.
          </Alert>
        )}

        {isHomecareClient && homecareQuery.isLoading && (
          <p className="mt-8 font-body text-ink-muted">Loading your account…</p>
        )}

        {isHomecareClient && homecareQuery.data && (
          <HomecareWorkspace
            data={homecareQuery.data}
            displayName={me.displayName ?? me.email}
          />
        )}

        {isHomecareClient && homecareQuery.isError && (
          <Alert tone="danger" title="Couldn't load your homecare account">
            Please refresh the page. If this keeps happening, contact us at{' '}
            {SUPPORT_EMAIL}.
          </Alert>
        )}
      </div>
    </PortalShell>
  );
}

interface CaregiverProfileData {
  legalGivenName: string;
  legalSurname: string;
  preferredName: string | null;
  gender: Gender | null;
  status: CaregiverStatus;
  phoneMobile: string | null;
  phoneAlternate: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  selfDescription: string | null;
  maxTravelKm: number;
  sinOnFile: boolean;
  taxIdentityOnFile: boolean;
  // Phase 11b-1 — `eftOnFile` is retained for backward compat (derives from
  // eftSubmittedAt !== null). `eftSubmitted` + `eftVerified` are the new
  // semantic split: caregiver has submitted ≠ operator has verified.
  eftOnFile: boolean;
  eftSubmitted: boolean;
  eftVerified: boolean;
  // Audit MEDIUM (2026-07-28) — rejection state so /me shows an action-required
  // banner instead of the calm "awaiting verification" one (rejection sets
  // eftRejectedAt without clearing eftSubmittedAt, so the two were identical).
  eftRejected: boolean;
  eftRejectionReason: string | null;
  hstRegistered: boolean;
  // Phase 5f-4 — status surface fields for banner copy
  applicationSubmittedAt: string | null;
  suspensionReason: string | null;
  roles: { code: string; payRateOverride: number | null }[];
  skills: string[];
  immunizations: string[];
}


/**
 * "Add your banking + tax info" banner. It sits in the persistent shell above
 * the tab strip on every tab and is deliberately non-dismissible (it self-
 * resolves once banking + SIN are on file). On mobile the full multi-line
 * message ate the top third of a 390px screen, so ship a slim single-line
 * variant that expands on tap; desktop keeps the full alert.
 */
function CaregiverBankingTaxBanner({ missingItems }: { missingItems: string[] }) {
  const [open, setOpen] = useState(false);
  // Audit M2 — list only what's actually outstanding (was hardcoded to all three).
  const needList = missingItems.length > 0 ? joinAnd(missingItems) : 'a few details';
  const detail = (
    <>
      We need {needList} on file so payouts can run. You can be matched to shifts
      without it, but we can't pay you until it's on file.{' '}
      <Link to="/profile" className="font-semibold text-forest underline underline-offset-2">
        Go to your profile →
      </Link>
    </>
  );
  return (
    <>
      {/* Mobile: slim, expand-on-tap */}
      <div className="sm:hidden">
        <Alert tone="warning">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex w-full items-center justify-between gap-2 text-left font-medium"
          >
            <span>Add your banking + tax info so we can pay you</span>
            <span aria-hidden="true" className="text-ink-muted">
              {open ? '▴' : '▾'}
            </span>
          </button>
          {open && <div className="mt-2 text-ink-muted">{detail}</div>}
        </Alert>
      </div>
      {/* Desktop: full alert */}
      <div className="hidden sm:block">
        <Alert tone="warning" title="Add your banking + tax info so we can pay you">
          {detail}
        </Alert>
      </div>
    </>
  );
}

/**
 * Phase 17 layout-C caregiver /me — flat working tabs in the shared
 * <PersonaWorkspace> shell. Working tabs (Offers/Schedule/Payouts/Earnings)
 * only appear when ACTIVE; Availability follows the existing section gate
 * (editable through onboarding); Profile & Docs is always present and is the
 * default landing tab for onboarding caregivers (what they must complete).
 */
function CaregiverWorkspace({ data }: { data: CaregiverProfileData }) {
  const gates = caregiverSectionGates(data.status);
  const isActive = data.status === 'ACTIVE';

  // Caregiver banner state — pure + unit-tested in lib/caregiver-banner.ts so the
  // hasBanner gate can't silently omit a sub-state (fix-verify High).
  const { needsTaxInfo, bankingRejected, awaitingVerification, hasBanner, missingItems } =
    caregiverBannerFlags(data);

  // Offers = new work → ACTIVE only. Schedule / Payouts / Earnings = history +
  // records → any POST-ONBOARDING status, so a SUSPENDED or TERMINATED caregiver
  // keeps access to past shifts + payout/earnings records (tax relevance).
  const postOnboarding =
    data.status === 'ACTIVE' ||
    data.status === 'SUSPENDED' ||
    data.status === 'TERMINATED';

  const tabs: PersonaWorkspaceTab[] = [];
  // Personal account (personal info + Payment & Tax + Security) moved to
  // /profile under the top-right user menu (Phase 17) — it is relatively
  // static and no longer belongs in the working-tab strip. The onboarding
  // banner below links applying caregivers there.

  // Phase 5g — ONBOARDING caregivers land on a "Get started" checklist tab
  // (contract + SIN + banking) that auto-activates them when complete.
  if (gates.showOnboarding) {
    tabs.push({
      key: 'onboarding',
      label: 'Get started',
      render: () => (
        <CaregiverOnboardingTab status={data.status} hstRegistered={data.hstRegistered} />
      ),
    });
  }
  if (isActive) {
    tabs.push({ key: 'offers', label: 'Offers', render: () => <OffersTab /> });
  }
  if (postOnboarding) {
    tabs.push({ key: 'schedule', label: 'Schedule', render: () => <ScheduleTab /> });
  }
  if (gates.showAvailabilityEdit) {
    tabs.push({
      key: 'availability',
      label: 'Availability',
      render: () => <CaregiverAvailabilitySection />,
    });
  }
  if (postOnboarding) {
    // Earnings = yearly totals + the individual payout statements, one view.
    // Earnings = one year picker → that year's summary → that year's payouts.
    tabs.push({ key: 'earnings', label: 'Earnings', render: () => <PayoutsTab /> });
  }
  // Docs = credential + compliance documents (T4A / tax docs to follow).
  // Audit F1 — shown for every pre-terminal state, INCLUDING UNDER_REVIEW and
  // ONBOARDING (view-only there), so /me is never a blank shell: an applicant
  // sits in UNDER_REVIEW for hours-to-days and previously saw zero tabs. The
  // sections inside enforce edit-vs-view via the section gates.
  if (
    data.status === 'APPLYING' ||
    data.status === 'UNDER_REVIEW' ||
    data.status === 'ONBOARDING' ||
    data.status === 'ACTIVE'
  ) {
    // Option B (Phase 17 bugfixes): the Docs tab is now a read-only STATUS glance
    // — a completion notice + each document's status — with uploads/management
    // living on /profile. So it's informative in every state and never exposes an
    // upload action here (that's gated on /profile via the section gates).
    tabs.push({
      key: 'docs',
      label: 'Docs',
      render: () => <CaregiverDocsTab />,
    });
  }

  // StatusBanner returns null for ACTIVE; only render the banner slot when
  // there's something to show (avoids an empty gap).
  const banner = hasBanner ? (
    <>
      <StatusBanner
        status={data.status}
        suspensionReason={data.suspensionReason}
        applicationSubmittedAt={data.applicationSubmittedAt}
      />
      {needsTaxInfo && <CaregiverBankingTaxBanner missingItems={missingItems} />}
      {bankingRejected && (
        <Alert tone="warning" title="Banking rejected — action required">
          Your void cheque couldn’t be verified
          {data.eftRejectionReason ? `: ${data.eftRejectionReason}` : ''}. Please
          re-submit your banking details in{' '}
          <Link
            to="/profile"
            className="font-semibold text-forest underline underline-offset-2"
          >
            your profile
          </Link>{' '}
          so your payouts can be released.
        </Alert>
      )}
      {awaitingVerification && (
        <Alert tone="info" title="Banking submitted — awaiting verification">
          Your banking is on file. An operator will verify the void cheque
          against your submission before your first payout runs.
        </Alert>
      )}
      {!isActive && !needsTaxInfo && !gates.showOnboarding && (
        <p className="font-body text-sm text-ink-muted">
          Manage your personal details, documents, and application in{' '}
          <Link to="/profile" className="font-semibold text-forest underline underline-offset-2">
            your profile
          </Link>
          .
        </p>
      )}
    </>
  ) : undefined;

  return (
    <PersonaWorkspace
      identity={{
        name: data.preferredName ?? data.legalGivenName,
        personaLabel: personaLabelForRoles(data.roles),
        statusBadge: <StatusBadge status={data.status} />,
      }}
      banner={banner}
      tabs={tabs}
      defaultTabKey={isActive ? 'offers' : (tabs[0]?.key ?? '')}
    />
  );
}

/** Account & application — personal info (edit-toggle) + Documents (credential +
 *  compliance uploads) + work experience + Payment & Tax (banking/SIN/HST) +
 *  Submit-for-review. Option B (Phase 17): document uploads live here on /profile
 *  (the /me "Docs" tab is now a read-only status glance); Security (password/2FA)
 *  moved to the /security route. Rendered on /profile. */
export function CaregiverProfileTab({ data }: { data: CaregiverProfileData }) {
  const gates = caregiverSectionGates(data.status);
  // One card: read-only by default; an "Edit" button toggles the editable form.
  const [editing, setEditing] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <Card padding="lg" accent="left" tone="forest">
        <CardHeader
          title={data.preferredName ?? data.legalGivenName}
          description={`${data.legalGivenName} ${data.legalSurname}`}
          action={
            gates.showProfileEdit && !editing ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setEditing(true)}
                data-testid="caregiver-profile-edit"
              >
                Edit
              </Button>
            ) : undefined
          }
        />

        {/* Profile photo — available in any lifecycle state (no status gate). */}
        <div className="mt-4">
          <CaregiverProfilePhoto
            name={data.preferredName ?? `${data.legalGivenName} ${data.legalSurname}`}
          />
        </div>

        {editing ? (
          <div className="mt-4">
            <CaregiverProfileEditForm
              initial={{
                preferredName: data.preferredName,
                phoneMobile: data.phoneMobile,
                phoneAlternate: data.phoneAlternate,
                addressLine1: data.addressLine1,
                addressLine2: data.addressLine2,
                city: data.city,
                province: data.province as 'ON' | null,
                postalCode: data.postalCode,
                selfDescription: data.selfDescription,
                gender: data.gender ?? undefined,
                maxTravelKm: data.maxTravelKm,
              }}
              onSuccess={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          </div>
        ) : (
          <>
            <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
              <ProfileField label="Status" value={data.status} />
              <ProfileField label="Gender" value={genderLabel(data.gender)} />
              <ProfileField label="Mobile phone" value={data.phoneMobile ?? '—'} />
              <ProfileField
                label="Roles"
                value={data.roles.map((r) => r.code).join(', ') || '—'}
              />
              <ProfileField label="Skills" value={data.skills.join(', ') || '—'} />
              {/* Read-only display of the travel radius so a caregiver can see
                  their current setting without opening the edit form (it governs
                  which shifts they're offered). Editable under Edit → "How far
                  will you travel?". */}
              <ProfileField
                label="Travel radius"
                value={`${String(data.maxTravelKm)} km`}
              />
            </dl>
            <p className="mt-4 font-body text-xs text-ink-muted">
              <strong>Roles</strong> (PSW / RPN / RN / Companion) come from your
              credentials and are set by TrueCare during vetting — contact us to
              change them. <strong>Skills</strong> (e.g. footcare, palliative-care
              experience) you declare yourself below under “Skills &amp;
              experience.”
            </p>
          </>
        )}
      </Card>

      {/* Documents — credentials + compliance. Option B (Phase 17 bugfixes): the
          uploads live here on /profile so an applying caregiver completes AND
          submits on ONE page; the /me Docs tab is now a read-only status glance.
          Editable while APPLYING/ACTIVE; frozen during UNDER_REVIEW/ONBOARDING. */}
      <CaregiverCredentialsSection readOnly={!gates.showCredentialsEdit} />
      <CaregiverComplianceSection readOnly={!gates.showComplianceEdit} />
      {/* Doc 16 — optional additional certificates. Same edit window as the
          other optional self-declarations (APPLYING / ONBOARDING / ACTIVE). */}
      <CaregiverCertificatesSection readOnly={!gates.showProfileEdit} />

      {/* Work experience + résumé (change-list 1.2). No status gate — it's part
          of the application, so APPLYING caregivers need it. */}
      <CaregiverSkillsSection
        roleCodes={data.roles.map((r) => r.code)}
        currentSkills={data.skills}
        editable={gates.showProfileEdit}
      />

      <CaregiverImmunizationSection
        currentImmunizations={data.immunizations}
        editable={gates.showProfileEdit}
      />

      <CaregiverWorkExperienceSection />

      {/* Payment & Tax — banking / SIN / HST self-service. Phase 5g (Q4 /
          change-list 1.4): collected in ONBOARDING, editable while ACTIVE,
          hidden before approval so the SIN field never shows pre-approval. */}
      {gates.showPaymentTax && (
        <CaregiverPaymentTaxSection
          status={data.status}
          hstRegistered={data.hstRegistered}
        />
      )}

      {/* Submit-for-review panel (APPLYING only). Uploads now sit above it on this
          same page, so the panel's readiness refetches the instant the last doc
          lands — no browser refresh needed (Phase 17 bug #3 closed structurally). */}
      {gates.showSubmitPanel && <CaregiverSubmitPanel />}
    </div>
  );
}

/** "Docs" tab (Option B) — a read-only STATUS glance. Uploads + management live on
 *  /profile; this tab shows a completion notice + each document's current status
 *  (uploaded / approved / expiring) and links back to /profile to act on them. */
function CaregiverDocsTab() {
  return (
    <div className="flex flex-col gap-6">
      <CaregiverDocsCompletionNotice />
      <CaregiverCredentialsSection readOnly />
      <CaregiverComplianceSection readOnly />
      <CaregiverCertificatesSection readOnly />
      {/* Bug #7 — the caregiver's own signed IC agreement (download). Renders only
          once they've accepted it; the accept flow itself lives in onboarding. */}
      <CaregiverSignedAgreementCard />
      <p className="font-body text-sm text-ink-muted">
        Upload or replace documents in{' '}
        <Link to="/profile" className="font-semibold text-forest underline underline-offset-2">
          your profile
        </Link>
        .
      </p>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-body text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </dt>
      <dd className="mt-0.5 font-body text-sm text-ink">{value}</dd>
    </div>
  );
}

interface FacilityProfileData {
  id: string;
  name: string;
  dbaName: string | null;
  type: string;
  status: string;
  hstTreatment: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string;
  postalCode: string | null;
  primaryContactName: string | null;
  primaryContactRole: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  billingContactEmail: string | null;
  currentContract: {
    effectiveFrom: string;
    effectiveTo: string | null;
    poReference: string | null;
    invoiceCadence: string;
    netTermsDays: number;
    dunningProfile: string;
    interestPerMonthPct: number;
    hasSignedDoc: boolean;
  } | null;
  users: {
    userId: string;
    email: string;
    displayName: string | null;
    invitedAt: string;
    acceptedAt: string | null;
  }[];
  /** RBAC 3.2 — the signed-in user's own capability set at this facility. Used
   *  to hide surfaces they can't use (the backend guard is the real gate). */
  myCapabilities: FacilityCapability[];
}


/**
 * Phase 17 facility /me — four flat tabs in the shared <PersonaWorkspace>
 * shell. A facility is an organization (many users share the operational
 * data); "Shifts" + "Invoices" are the shared operations, "Facility" is the
 * org record (read-only in v1 — edits + user invites are operator-managed),
 * and "Profile" is the signed-in user's own account.
 */
function FacilityWorkspace({ data }: { data: FacilityProfileData }) {
  const isActive = data.status === 'ACTIVE';
  // RBAC 3.2 — hide what this login can't do. The backend guard enforces; this
  // just avoids showing a tab that would 403. A Payer has INVOICE_VIEW but not
  // SHIFT_VIEW, so the Shifts tab is gated too, not only its create actions.
  const caps = data.myCapabilities;
  // RBAC 3.2 — the tab-visibility + default-tab decision is a pure, exhaustively
  // tested helper (facility-workspace-tabs.spec) so a regression can't ship a
  // login onto a tab whose panel 403s.
  const { visible, defaultTabKey } = facilityWorkspaceTabPlan(caps, isActive);
  const canViewShifts = visible.includes('shifts');
  const canViewInvoices = visible.includes('invoices');
  const canManageUsers = visible.includes('users');

  const banner =
    data.status !== 'ACTIVE' ? (
      <Alert tone="warning" title={`This facility is ${data.status.toLowerCase()}`}>
        {data.status === 'PROSPECT'
          ? 'Your facility account is in review. TrueCare™ will reach out to finalise the contract before you can post shifts.'
          : data.status === 'SUSPENDED'
            ? 'Shift posting is paused on this facility. Existing shifts continue; contact TrueCare™ to resume.'
            : 'This facility is no longer active. Reach out to TrueCare™ if you believe this is an error.'}
      </Alert>
    ) : undefined;

  // Personal account (the signed-in user's own profile + security) moved to
  // /profile under the top-right user menu (Phase 17). The "Facility" tab
  // below is the shared ORG record and stays here.
  const tabs: PersonaWorkspaceTab[] = [
    { key: 'facility', label: 'Facility', render: () => <FacilityInfoTab data={data} /> },
    // Shifts tab requires SHIFT_VIEW (Coordinator/Approver/Admin — not Payer).
    ...(canViewShifts
      ? [
          {
            key: 'shifts' as const,
            label: 'Shifts',
            render: () => (
              <BuyerShiftPanel
                persona="FACILITY"
                showTitle={false}
                canPost={isActive}
                capabilities={caps}
              />
            ),
          },
        ]
      : []),
    // Invoices tab only for a login with INVOICE_VIEW (Payer / Admin). A
    // Coordinator or Approver never sees it.
    ...(canViewInvoices
      ? [
          {
            key: 'invoices' as const,
            label: 'Invoices',
            render: () => <BuyerInvoicesTab persona="facility" />,
          },
        ]
      : []),
    // Users tab only for a facility Admin (ACCOUNT_MANAGE) — self-service team
    // management (facility Admins manage their own logins, not the operator).
    ...(canManageUsers
      ? [
          {
            key: 'users' as const,
            label: 'Users',
            render: () => <FacilityUsersTab />,
          },
        ]
      : []),
  ];

  return (
    <PersonaWorkspace
      identity={{
        name: data.name,
        personaLabel: 'Facility',
        statusBadge: <StatusBadge status={data.status} />,
      }}
      banner={banner}
      tabs={tabs}
      defaultTabKey={defaultTabKey}
    />
  );
}

/** Personal account — the signed-in user's own account, separate from the org.
 *  Rendered on /profile (Phase 17). */
export function FacilityAccountTab() {
  return (
    <div className="flex flex-col gap-6">
      <AccountProfileCard />
    </div>
  );
}

/** "Facility" tab — the organization record: details, contract, users. All
 *  read-only in v1 (edits + user invites are operator-managed). */
function FacilityInfoTab({ data }: { data: FacilityProfileData }) {
  return (
    <div className="flex flex-col gap-6">
      <Card padding="lg" accent="left" tone="forest">
        <CardHeader
          title={data.name}
          description={data.dbaName ? `Operating as ${data.dbaName}` : data.type.replaceAll('_', ' ')}
        />
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
          <ProfileField label="Status" value={data.status} />
          <ProfileField label="Type" value={data.type.replaceAll('_', ' ')} />
          <ProfileField
            label="Tax status"
            value={
              data.hstTreatment === 'EXEMPT'
                ? 'Exempt (no HST)'
                : data.hstTreatment === 'ZERO_RATED'
                  ? 'Zero-rated (0%)'
                  : 'Taxable (13% HST)'
            }
          />
          <ProfileField label="Address" value={formatAddress(data) || '—'} />
          <ProfileField label="Primary contact" value={data.primaryContactName ?? '—'} />
          <ProfileField label="Contact email" value={data.primaryContactEmail ?? '—'} />
          <ProfileField
            label="Billing email"
            value={data.billingContactEmail ?? data.primaryContactEmail ?? '—'}
          />
        </dl>
        {/* Phase 14b-2-7 (B5) — explainer copy for the intentional v1
            limitation: facility-details edits are operator-mediated per
            Q9.A (signed-contract negotiation belongs offline). */}
        <p
          className="mt-4 text-sm text-ink-muted"
          data-testid="facility-details-operator-managed-note"
        >
          To update facility details (name, address, contacts, billing
          email), contact your TrueCare™ account manager.
        </p>
      </Card>

      <Card padding="lg" accent="left" tone="forest">
        <CardHeader
          title="Contract"
          description={
            data.currentContract
              ? `In effect since ${data.currentContract.effectiveFrom}`
              : 'No active contract yet — TrueCare™ is preparing one for signature.'
          }
        />
        {data.currentContract && (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
            <ProfileField
              label="PO reference"
              value={data.currentContract.poReference ?? '—'}
            />
            <ProfileField label="Invoice cadence" value={data.currentContract.invoiceCadence} />
            <ProfileField
              label="Payment terms"
              value={`Net ${String(data.currentContract.netTermsDays)} days`}
            />
            <ProfileField label="Dunning profile" value={data.currentContract.dunningProfile} />
            <ProfileField
              label="Late interest"
              value={`${String(data.currentContract.interestPerMonthPct)}% / month`}
            />
          </dl>
        )}
        {data.currentContract?.hasSignedDoc && (
          <div className="mt-4">
            <Button
              size="sm"
              variant="secondary"
              data-testid="facility-download-signed-contract"
              onClick={() => {
                void (async () => {
                  try {
                    const res = (await unwrap(
                      api.GET('/api/v1/me/facility/profile/contract-doc'),
                    )) as unknown as { downloadUrl: string };
                    window.open(res.downloadUrl, '_blank', 'noopener');
                  } catch {
                    // Best-effort — the link simply won't open if the doc was
                    // pulled between page load and click.
                  }
                })();
              }}
            >
              Download signed contract
            </Button>
          </div>
        )}
      </Card>

      <Card padding="lg" accent="left" tone="forest">
        <CardHeader
          title="Portal users"
          description={`${String(data.users.length)} ${data.users.length === 1 ? 'person has' : 'people have'} access to this facility's portal.`}
        />
        <ul className="flex flex-col gap-2">
          {data.users.map((u) => (
            <li
              key={u.userId}
              className="flex items-baseline justify-between gap-4 border-b border-border pb-2 last:border-b-0 last:pb-0"
            >
              <div>
                <div className="font-body text-sm text-ink">
                  {u.displayName ?? u.email}
                </div>
                <div className="font-body text-xs text-ink-muted">{u.email}</div>
              </div>
              <div className="font-body text-xs text-ink-muted whitespace-nowrap">
                {u.acceptedAt ? `joined ${formatDate(u.acceptedAt)}` : 'invite pending'}
              </div>
            </li>
          ))}
        </ul>
        {/* Phase 14b-2-7 (B4) — explainer copy for the intentional v1
            limitation: self-serve portal-user invite isn't shipped (per
            Q7's no-multi-user-with-roles lock); operator invites via the
            admin endpoint POST /admin/facilities/:id/users. */}
        <p
          className="mt-4 text-sm text-ink-muted"
          data-testid="portal-users-operator-managed-note"
        >
          To add or remove portal users, contact your TrueCare™ account
          manager.
        </p>
      </Card>
    </div>
  );
}

function formatAddress(data: FacilityProfileData): string {
  const parts = [data.addressLine1, data.addressLine2, data.city, data.province, data.postalCode]
    .filter((p): p is string => Boolean(p));
  return parts.join(', ');
}

// ── Homecare ─────────────────────────────────────────────────────────────────


/**
 * Phase 17 homecare /me — four flat tabs in the shared <PersonaWorkspace> shell.
 * A homecare client is ONE individual (the client is the user) with N care
 * recipients + Stripe payment method(s). "Visits" + "Billing" are operational,
 * "Recipients" are the people care is booked for, "Profile" is the client's own
 * account. The Billing tab hosts self-service card management (Stripe Elements)
 * + invoice history; a default card is required before a STRIPE-mode client can
 * book (server-enforced, mirrored on the Book button).
 */
function HomecareWorkspace({
  data,
  displayName,
}: {
  data: HomecareMeResponse;
  displayName: string;
}) {
  const isActive = data.status === 'ACTIVE';
  // Phase 23 (23-6) — booking is gated on a current Client Agreement acceptance.
  const agreement = useHomecareAgreement();
  const needsAgreement =
    isActive && !agreement.isLoading && !agreement.accepted;
  // Payment gate — a STRIPE-mode client must have a default card on file before
  // booking (the server enforces the same gate). INVOICE-mode clients are
  // AR-billed and need no card. Shown once the agreement is accepted.
  const needsPaymentMethod =
    isActive &&
    data.paymentMode === 'STRIPE' &&
    !data.hasDefaultPaymentMethod;

  const banner =
    data.status === 'SUSPENDED' ? (
      <Alert tone="warning" title="Your account is paused">
        {data.suspensionReason ?? 'Contact TrueCare™ to restore your account.'} You
        can still view your information but can't add or change recipients while
        the account is paused.
      </Alert>
    ) : data.status === 'TERMINATED' ? (
      <Alert tone="danger" title="Your account is closed">
        {data.suspensionReason ??
          'Contact TrueCare™ if you believe this is an error.'}
      </Alert>
    ) : needsAgreement ? (
      // Prominent accept card until signed; the Visits/Book tab stays hidden.
      <HomecareAgreementCard />
    ) : needsPaymentMethod ? (
      // The flag is false for any of: no cards, cards but none default, or an
      // expired default. me.tsx only has the boolean, so keep the banner generic
      // and point at the Billing tab, where the specific state (add / set default
      // / replace expired) is spelled out.
      <Alert tone="warning" title="A valid payment method is needed to book">
        Visit charges are applied automatically to your default card. Add a card,
        replace an expired one, or set a default on the <strong>Billing</strong>{' '}
        tab to start booking.
      </Alert>
    ) : undefined;

  const recipientsForPicker = (data.recipients ?? []).map((r) => ({
    id: r.id,
    name: r.name,
    // Keep the geocoded flag so the Post-a-visit picker can block an
    // ungeocoded (unmatchable) recipient.
    geocoded: r.geocoded,
  }));

  // Personal account (display name, payment/preferences, security) moved to
  // /profile under the top-right user menu (Phase 17).
  const tabs: PersonaWorkspaceTab[] = [
    {
      key: 'shifts',
      label: 'Visits',
      render: () => (
        <BuyerShiftPanel
          persona="HOMECARE"
          recipients={recipientsForPicker}
          showTitle={false}
          // 23-6 — no booking until the Client Agreement is accepted, and (for
          // STRIPE-mode clients) a default card is on file — the server gates
          // on both, so mirror them here to keep the Book button honest.
          canPost={isActive && agreement.accepted && !needsPaymentMethod}
          // Distinguish the agreement gate from a suspended account. Audit #3 —
          // only point to the accept card when it's actually shown
          // (needsAgreement, i.e. not still loading); during the initial
          // agreement fetch show neutral copy instead of pointing at a card
          // that hasn't mounted yet.
          postPausedMessage={
            needsAgreement
              ? 'Please accept the Client Agreement above before booking your first visit.'
              : needsPaymentMethod
                ? 'Add or update a valid default payment method on the Billing tab before booking.'
                : isActive && agreement.isLoading
                  ? 'Checking your account…'
                  : undefined
          }
        />
      ),
    },
    {
      // Payment methods (self-service card management) + invoice history live
      // together under "Billing" — the label was "Invoices" but the tab now
      // leads with card management, which is what a client needs before booking.
      key: 'invoices',
      label: 'Billing',
      render: () => (
        <>
          <HomecarePaymentMethods />
          {/* Audit L3 — stacked under Payment methods, so railed+gold to satisfy
              the Card all-or-none rule (both billing cards get a gold rail). */}
          <BuyerInvoicesTab persona="homecare" railed />
        </>
      ),
    },
    {
      key: 'recipients',
      label: 'Recipients',
      render: () => (
        <RecipientsSection
          recipients={data.recipients}
          editable={data.status === 'ACTIVE'}
          // Audit #19 — Remove (archive) is allowed on SUSPENDED accounts; only
          // TERMINATED blocks it, matching the backend archiveRecipient guard.
          removable={data.status !== 'TERMINATED'}
        />
      ),
    },
  ];

  return (
    <PersonaWorkspace
      identity={{
        name: displayName,
        personaLabel: 'Homecare',
        statusBadge: <StatusBadge status={data.status} />,
      }}
      banner={banner}
      tabs={tabs}
      defaultTabKey="shifts"
    />
  );
}

/** Personal account — the homecare client's own account: display name + email,
 *  payment + preferences, and security. Rendered on /profile (Phase 17). */
export function HomecareProfileTab({ data }: { data: HomecareMeResponse }) {
  return (
    <div className="flex flex-col gap-6">
      <AccountProfileCard />
      <HomecareAccountSummary data={data} />
    </div>
  );
}
